import { useEntity } from '@backstage/plugin-catalog-react';
import { makeStyles, Paper, Typography } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

/*
 * E8 — frontend half of the non-dismissible "UNVALIDATED — not endorsed" marker.
 *
 * Renders a prominent banner on the catalog entity page of any AI-Asset-Library
 * entry whose (server-computed, fail-closed) `governance.asg/validation-status`
 * annotation is not `effective`. The banner has NO close/collapse control by
 * construction — that is the "non-dismissible" guarantee condition E8 requires
 * (panel LG-ENG-REVIEW-AIAssetLibrary-2026-07-03b, Security SC-3). The annotation
 * is computed by the backend ValidationStatusProcessor, so it cannot be dodged by
 * omitting it: a library entity with no/unknown status resolves to `unvalidated`.
 */

export const VALIDATION_STATUS_ANNOTATION = 'governance.asg/validation-status';

type Severity = 'danger' | 'warning';
export type Marker = { severity: Severity; title: string; body: string };

const MARKERS: Record<string, Marker> = {
  listed: {
    severity: 'danger',
    title: 'UNVALIDATED — not endorsed',
    body: 'This entry is Listed: captured and secret-scanned, but it has not passed the validation gate (an eval bound to a model version for prompts; the Governance Gate Contract + tests for components). It carries no governance endorsement. Use with judgement — do not treat it as an approved standard.',
  },
  unvalidated: {
    severity: 'danger',
    title: 'UNVALIDATED — unrecognised status',
    body: 'This entry does not declare a recognised status (Listed | Effective | Stale | Superseded). It is treated as UNVALIDATED until corrected — no governance endorsement.',
  },
  stale: {
    severity: 'warning',
    title: 'STALE — expired, pending re-validation',
    body: 'This entry was Blessed, but its bound model or standards version was superseded and it is past its re-validation SLA. It no longer carries current endorsement until re-evaluated.',
  },
  superseded: {
    severity: 'warning',
    title: 'SUPERSEDED — see successor',
    body: 'This entry has been superseded by a newer entry and is retained only for traceability. Use its successor instead.',
  },
};

/**
 * Marker to render for a validation-status value, or undefined when none applies
 * (a Blessed `effective` entry, or a value with no annotation at all). Fail-closed:
 * any other non-empty, non-`effective` value renders the UNVALIDATED marker.
 */
export function markerFor(status?: string): Marker | undefined {
  if (!status) return undefined;
  const key = status.trim().toLowerCase();
  if (key === '' || key === 'effective') return undefined; // Blessed / none → no marker
  return MARKERS[key] ?? MARKERS.unvalidated; // fail-closed
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeftWidth: theme.spacing(0.75),
    borderLeftStyle: 'solid',
  },
  danger: {
    borderLeftColor: theme.palette.error.main,
    backgroundColor: `${theme.palette.error.main}14`, // ~8% alpha
  },
  warning: {
    borderLeftColor: theme.palette.warning.main,
    backgroundColor: `${theme.palette.warning.main}14`,
  },
  icon: { marginTop: theme.spacing(0.25) },
  dangerIcon: { color: theme.palette.error.main },
  warningIcon: { color: theme.palette.warning.main },
  title: { fontWeight: 700 },
}));

export const ValidationStatusBanner = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const status = entity.metadata.annotations?.[VALIDATION_STATUS_ANNOTATION];
  const marker = markerFor(status);
  if (!marker) return null; // effective or non-library entity → render nothing

  const isDanger = marker.severity === 'danger';
  const Icon = isDanger ? ErrorOutlineIcon : WarningRoundedIcon;
  return (
    <Paper
      elevation={0}
      variant="outlined"
      role="alert"
      aria-live="polite"
      className={`${classes.root} ${isDanger ? classes.danger : classes.warning}`}
    >
      <Icon
        fontSize="large"
        className={`${classes.icon} ${
          isDanger ? classes.dangerIcon : classes.warningIcon
        }`}
      />
      <div>
        <Typography variant="subtitle1" className={classes.title}>
          {marker.title}
        </Typography>
        <Typography variant="body2">{marker.body}</Typography>
      </div>
    </Paper>
  );
};
